import React from 'react';
import { Book, Loader2, ShoppingCart, Check } from 'lucide-react';
import { useGuideProduct } from '../../../hooks/useGuideProduct';
import { useNavigate } from 'react-router-dom';

interface GuideProductCardProps {
  plantId: string;
  plantName: string;
}

export default function GuideProductCard({ plantId, plantName }: GuideProductCardProps) {
  const navigate = useNavigate();
  const { product, loading } = useGuideProduct(plantId);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
      </div>
    );
  }

  // Show placeholder when no guide exists
  if (!product) {
    return (
      <div className="bg-gradient-to-br from-primary-50 to-white rounded-lg shadow-sm overflow-hidden border border-primary-100">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="inline-flex items-center gap-2 bg-primary-500 text-white px-3 py-1.5 rounded-full text-sm font-medium">
              <Book className="h-4 w-4" />
              <span>Coming Soon!</span>
            </div>
          </div>
          
          <h3 className="text-lg font-semibold mb-2">Comprehensive Guide to Growing {plantName}</h3>
          <p className="text-gray-600 text-sm">
            Everything you need to know for how to grow {plantName} is coming soon! Check back soon for our expert-backed guide.
          </p>
        </div>
      </div>
    );
  }

  const handleBuyNow = () => {
    navigate(`/shop/${product.slug}`);
  };

  return (
    <div className="bg-gradient-to-br from-primary-50 to-white rounded-lg shadow-md overflow-hidden border border-primary-100 transform transition-all hover:shadow-lg hover:-translate-y-0.5">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="inline-flex items-center gap-2 bg-primary-500 text-white px-3 py-1.5 rounded-full text-sm font-medium animate-pulse">
            <Book className="h-4 w-4" />
            <span>Premium Guide</span>
          </div>
          <div className="ml-auto inline-flex items-center gap-1 text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
            <Check className="h-3 w-3" />
            <span>Expert Verified</span>
          </div>
        </div>

        <h3 className="text-xl font-bold mb-2 text-gray-900">{product.name}</h3>
        <p className="text-sm text-gray-600 mb-4">Over 30 pages of growing advice -- from seed starting to preservation!</p>
        
        <div className="space-y-2 mb-4">
          <div className="flex items-start gap-2">
            <Check className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-600">Step-by-step growing instructions</p>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-600">Troubleshooting common problems</p>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-600">Season-by-season care calendar</p>
          </div>
          <div className="flex items-start gap-2">
            <Check className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
            <p className="text-gray-600">Great for beginner or advanced gardeners</p>
          </div>
        </div>
        
        <div className="flex items-center justify-between mt-6">
          <div>
            <div className="text-2xl font-bold text-primary-600">
              ${product.price.toFixed(2)}
            </div>
            <p className="text-xs text-gray-500">One-time purchase</p>
          </div>
          <button
            onClick={handleBuyNow}
            className="flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-all transform hover:scale-105 font-medium shadow-md hover:shadow-lg"
          >
            <ShoppingCart className="h-5 w-5" />
            <span>Download the Expert Guide!</span>
          </button>
        </div>
      </div>
    </div>
  );
}